import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  inject,
  AfterViewInit,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

import { TagModel } from '@core/models/tag.model';
import { TaskModel } from '@core/models/task.model';
import { UserModel } from '@core/models/user.model';
import { EditorJsPreviewPipe } from '@core/pipes/editor-js-preview.pipe';
import * as fromRoot from '@core/redux';
import * as TaskActions from '@core/redux/task/task.actions';
import { Store } from '@ngrx/store';
import { AvatarComponent } from '@ux/components/avatar/avatar.component';
import { TagComponent } from '@ux/components/tag/tag.component';
import { TaskPlusComponent } from '@ux/components/task-plus/task-plus.component';
import { ImagePreviewPipe } from '@ux/pipes/image-preview.pipe';

interface TaskFormEvent {
  index: number;
  afterId: string;
}

@Component({
  selector: 'app-board-card',
  templateUrl: './board-card.component.html',
  styleUrls: ['./board-card.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, TaskPlusComponent, TagComponent, AvatarComponent, FormsModule, EditorJsPreviewPipe],
})
export class BoardCardComponent implements AfterViewInit {
  private readonly store = inject<Store<fromRoot.State>>(Store);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly imagePreviewPipe = inject(ImagePreviewPipe);

  @Input({ required: true }) model!: TaskModel;
  @Input() taskId?: string;
  @Input() index = 0;
  @Input() items: TaskModel[] = [];

  @Output() allowUpdate = new EventEmitter<boolean>();
  @Output() onShowTaskFormEmitter = new EventEmitter<TaskFormEvent>();

  @ViewChild('textarea', { static: false }) textarea?: ElementRef<HTMLTextAreaElement>;

  protected readonly isSending = toSignal(this.store.select(fromRoot.getTaskLoading), { initialValue: false });

  constructor() {}

  ngAfterViewInit() {
    if (this.textarea) {
      this.textarea.nativeElement.focus();
    }
  }

  onBlur(model: TaskModel): void {
    this.allowUpdateList();

    if (model.name.length === 0) {
      this.items.splice(model.insertIndex, 1);
      return;
    }

    if (!this.isSending()) {
      this.store.dispatch(new TaskActions.CreateAction(model));
    }
  }

  onSave(model: TaskModel, event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    if (this.isSending()) {
      return;
    }

    this.allowUpdateList();
    const newTask = { ...model } as TaskModel;
    this.store.dispatch(new TaskActions.CreateAction(newTask));
  }

  onFilterByUser(user: UserModel | undefined, event: Event): void {
    if (!user) {
      return;
    }

    event.stopPropagation();
    this.updateRouteFilter({ responsibleId: user.id });
  }

  onFilterByTag(tag: TagModel, event: Event): void {
    event.stopPropagation();
    this.updateRouteFilter({ tagId: tag.id });
  }

  onShowTaskForm(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();

    this.onShowTaskFormEmitter.emit({
      index: this.index + 1,
      afterId: String(this.model.id),
    });
  }

  private allowUpdateList(): void {
    this.allowUpdate.emit(true);
  }

  private updateRouteFilter(queryParams: Record<string, string>): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
    });
  }

  getFirstImageUrl(): string | null {
    if (!this.model.files?.length) {
      return null;
    }

    const firstImage = this.model.files.find(file => file.isImage);
    if (!firstImage) {
      return null;
    }

    return this.imagePreviewPipe.transform(firstImage, false, false);
  }
}
